import { useCallback, useEffect, useState } from 'react';
import api from '../services/api.js';

const when = (ts) =>
  ts ? new Date(ts).toLocaleString('en-US', { timeZone: 'Asia/Manila', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—';
const amount = (q, unit) => `${Number(q).toLocaleString('en-US', { maximumFractionDigits: 2 })}${unit ? ` ${unit}` : ''}`;
const EMPTY = { item: '', qty: '', unit: '', from: '', to: '', note: '' };
const parseEnd = (v) => {
  const [type, id] = v.split(':');
  return { type, id };
};

/** Shore engineering: send items between stores and tugs, see what is awaiting
 * receipt and today's movements, and maintain the list of stores. */
export default function InventoryOffice() {
  const [locations, setLocations] = useState({ stores: [], tugs: [] });
  const [items, setItems] = useState([]);
  const [pending, setPending] = useState([]);
  const [today, setToday] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [store, setStore] = useState({ name: '', code: '' });
  const [msg, setMsg] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [loc, pend, moves, sug] = await Promise.all([
        api.getInventoryLocations(),
        api.getInventoryPending(),
        api.getInventoryMovements(),
        api.getInventorySuggestions(),
      ]);
      setLocations(loc);
      setPending(pend);
      setToday(moves);
      setItems(sug.items || []);
    } catch (e) {
      setMsg({ type: 'danger', text: e.message || 'Failed to load inventory.' });
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const fail = (err, fallback) => setMsg({ type: 'danger', text: err.details?.[0]?.message || err.message || fallback });

  const send = async (e) => {
    e.preventDefault();
    setMsg(null);
    if (!form.item.trim() || !form.qty || !form.from || !form.to) {
      setMsg({ type: 'danger', text: 'Item, quantity, from and to are required.' });
      return;
    }
    setSaving(true);
    try {
      const sent = await api.createInventoryTransfer({
        item: form.item.trim(),
        qty: Number(form.qty),
        unit: form.unit.trim() || null,
        from: parseEnd(form.from),
        to: parseEnd(form.to),
        note: form.note.trim() || null,
      });
      setMsg({ type: 'success', text: `${sent.item} sent to ${sent.to}. It stays "in transit" until they confirm receipt.` });
      setForm({ ...EMPTY, from: form.from });
      await load();
    } catch (err) {
      fail(err, 'Failed to send the item.');
    } finally {
      setSaving(false);
    }
  };

  const addStore = async () => {
    setMsg(null);
    if (!store.name.trim() || !store.code.trim()) {
      setMsg({ type: 'danger', text: 'A store needs a name and a short code.' });
      return;
    }
    try {
      await api.addInventoryStore({ name: store.name.trim(), code: store.code.trim() });
      setStore({ name: '', code: '' });
      await load();
    } catch (err) {
      fail(err, 'Failed to add the store.');
    }
  };

  const receive = async (id) => {
    setMsg(null);
    try {
      await api.receiveInventory(id, {});
      await load();
    } catch (err) {
      fail(err, 'Failed to confirm receipt.');
    }
  };

  const options = (
    <>
      <option value="">Select…</option>
      <optgroup label="Stores">
        {locations.stores.map((s) => <option key={s.id} value={`store:${s.id}`}>{s.name} ({s.code})</option>)}
      </optgroup>
      <optgroup label="Tugs">
        {locations.tugs.map((t) => <option key={t.id} value={`tug:${t.id}`}>{t.name} ({t.code})</option>)}
      </optgroup>
    </>
  );

  return (
    <div className="card border-0 shadow-sm" data-testid="inventory-office">
      <div className="card-header bg-white pt-3 border-bottom-0">
        <h6 className="mb-0 fw-bold text-uppercase small text-secondary">Inventory Transfers</h6>
      </div>
      <div className="card-body">
        {msg && <div className={`alert alert-${msg.type} py-2`} role="alert" data-testid="inventory-message">{msg.text}</div>}
        <form className="row g-2 align-items-end mb-3" onSubmit={send} noValidate>
          <div className="col-md-3">
            <label htmlFor="inv-item" className="form-label small fw-bold text-muted">Item</label>
            <input id="inv-item" list="inv-items" className="form-control form-control-sm" maxLength={200} value={form.item} onChange={(e) => setForm({ ...form, item: e.target.value })} data-testid="inv-item" />
            <datalist id="inv-items">{items.map((i) => <option key={i} value={i} />)}</datalist>
          </div>
          <div className="col-md-1">
            <label htmlFor="inv-qty" className="form-label small fw-bold text-muted">Qty</label>
            <input id="inv-qty" type="number" min="0" step="any" className="form-control form-control-sm" value={form.qty} onChange={(e) => setForm({ ...form, qty: e.target.value })} data-testid="inv-qty" />
          </div>
          <div className="col-md-1">
            <label htmlFor="inv-unit" className="form-label small fw-bold text-muted">Unit</label>
            <input id="inv-unit" className="form-control form-control-sm" maxLength={24} placeholder="pc, L…" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} data-testid="inv-unit" />
          </div>
          <div className="col-md-3">
            <label htmlFor="inv-from" className="form-label small fw-bold text-muted">From</label>
            <select id="inv-from" className="form-select form-select-sm" value={form.from} onChange={(e) => setForm({ ...form, from: e.target.value })} data-testid="inv-from">{options}</select>
          </div>
          <div className="col-md-3">
            <label htmlFor="inv-to" className="form-label small fw-bold text-muted">To</label>
            <select id="inv-to" className="form-select form-select-sm" value={form.to} onChange={(e) => setForm({ ...form, to: e.target.value })} data-testid="inv-to">{options}</select>
          </div>
          <div className="col-md-1">
            <button type="submit" className="btn btn-primary btn-sm w-100" disabled={saving} data-testid="inv-send">Send</button>
          </div>
        </form>

        <div className="row g-2 align-items-end mb-3 small">
          <div className="col-md-4">
            <label htmlFor="store-name" className="form-label small fw-bold text-muted">Add a store (warehouse or branch)</label>
            <input id="store-name" className="form-control form-control-sm" placeholder="Name" maxLength={160} value={store.name} onChange={(e) => setStore({ ...store, name: e.target.value })} data-testid="store-name" />
          </div>
          <div className="col-md-2">
            <label htmlFor="store-code" className="form-label small fw-bold text-muted">Code</label>
            <input id="store-code" className="form-control form-control-sm" placeholder="HSC" maxLength={8} value={store.code} onChange={(e) => setStore({ ...store, code: e.target.value })} data-testid="store-code" />
          </div>
          <div className="col-md-2">
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={addStore} data-testid="store-add">Add store</button>
          </div>
        </div>

        <div className="small fw-bold text-uppercase text-secondary mb-1">Awaiting receipt</div>
        {pending.length === 0 ? (
          <p className="text-muted small" data-testid="inv-none-pending">Nothing in transit.</p>
        ) : (
          <ul className="list-unstyled small">
            {pending.map((p) => (
              <li key={p.id} data-testid="inv-pending-row" data-item={p.item}>
                <span className="fw-bold">{p.item}</span> ({amount(p.qty, p.unit)}) · {p.from} → {p.to} · sent {when(p.sentAt)}
                {!p.toTugId && (
                  <button type="button" className="btn btn-link btn-sm p-0 ms-2" onClick={() => receive(p.id)} data-testid="inv-store-receive">Confirm received</button>
                )}
              </li>
            ))}
          </ul>
        )}

        <div className="small fw-bold text-uppercase text-secondary mb-1">Today</div>
        {today.filter((m) => m.status !== 'in_transit').length === 0 ? (
          <p className="text-muted small mb-0">No receipts or usage recorded today.</p>
        ) : (
          <ul className="list-unstyled small mb-0">
            {today.filter((m) => m.status !== 'in_transit').map((m) => (
              <li key={m.id} data-testid="inv-today-row" data-status={m.status}>
                {m.kind === 'used' ? (
                  <><span className="fw-bold">{m.item}</span> ({amount(m.qty, m.unit)}) used up on {m.from}</>
                ) : (
                  <>
                    <span className="fw-bold">{m.item}</span> · {m.from} → {m.to} · received {amount(m.receivedQty, m.unit)}
                    {m.discrepancy ? <span className="text-danger fw-semibold"> of {amount(m.qty, m.unit)} sent</span> : null}
                    {m.storedIn ? ` · stored in ${m.storedIn}` : ''}
                    {m.receiptNote ? ` · ${m.receiptNote}` : ''}
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
